import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  FiUpload, FiTrash2, FiArrowUp, FiArrowDown, FiPaperclip, FiDownload,
} from 'react-icons/fi';
import { API_URL } from '../config';

// What the backend's ALLOWED_LETTER_ATTACHMENT_EXTENSIONS accepts. Kept in step
// with app.py — the server rejects anything else regardless of what this offers.
const ACCEPT = '.pdf,.dwg,.dxf,.doc,.docx,.xls,.xlsx,.csv,.txt,.kmz,.kml,.zip';

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/**
 * Files enclosed with a letter — a PDF sketch, a plat, a KMZ.
 *
 * These do NOT render inside the letter the way photos do. The letter lists
 * them as lettered enclosures ("Exhibit A — Site Sketch") beneath the
 * signature, and the real file rides along on the sent email, so a multi-page
 * PDF stays a PDF. Anything that should print IN the letter belongs on the
 * photo panel instead.
 */
function LetterAttachmentPanel({ letterId, attachments, onAttachmentsChange, onRequireSave }) {
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [drafts, setDrafts] = useState({});   // id -> label being typed
  const fileRef = useRef();

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const reload = useCallback(async (id) => {
    const res = await axios.get(`${API_URL}/letters/${id}/attachments`, config);
    onAttachmentsChange(res.data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAttachmentsChange]);

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    // A brand-new letter has no id yet; save it so the files have somewhere to go.
    let id = letterId;
    if (!id) {
      id = await onRequireSave();
      if (!id) return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('file', file);
        await axios.post(`${API_URL}/letters/${id}/attachments`, form, {
          headers: { ...config.headers, 'Content-Type': 'multipart/form-data' },
        });
      }
      await reload(id);
    } catch (err) {
      alert('Attachment upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const saveLabel = async (att) => {
    const draft = drafts[att.id];
    if (draft === undefined || draft === (att.label || '')) return;
    setBusyId(att.id);
    try {
      await axios.patch(`${API_URL}/letters/${att.letter_id}/attachments/${att.id}`,
        { label: draft }, config);
      await reload(att.letter_id);
    } catch (err) {
      alert('Could not rename: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (att) => {
    if (!window.confirm(`Remove "${att.display_label}" from this letter?`)) return;
    setBusyId(att.id);
    try {
      await axios.delete(`${API_URL}/letters/${att.letter_id}/attachments/${att.id}`, config);
      await reload(att.letter_id);
    } catch (err) {
      alert('Could not delete: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyId(null);
    }
  };

  // The file endpoint needs a bearer token, so a plain href would 401. Pull it
  // as a blob and hand the browser a temporary object URL instead.
  const download = async (att) => {
    setBusyId(att.id);
    try {
      const res = await axios.get(
        `${API_URL}/letters/${att.letter_id}/attachments/${att.id}/file`,
        { ...config, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_filename || 'attachment';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      alert('Could not open the file: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const move = async (index, delta) => {
    const list = [...attachments];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    onAttachmentsChange(list); // optimistic, so the arrows feel instant
    try {
      const res = await axios.post(
        `${API_URL}/letters/${letterId}/attachments/reorder`,
        { order: list.map(a => a.id) }, config,
      );
      onAttachmentsChange(res.data || list);
    } catch (err) {
      alert('Could not reorder: ' + (err.response?.data?.error || err.message));
      reload(letterId);
    }
  };

  const labelStyle = {
    display: 'block', fontSize: '11px', textTransform: 'uppercase',
    color: '#6b7280', fontWeight: 600, marginBottom: '4px',
  };

  return (
    <div style={{ marginBottom: '20px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle}>Enclosures</label>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {attachments?.length
              ? `${attachments.length} file${attachments.length === 1 ? '' : 's'} sent with the letter`
              : 'Attach a PDF, plat or drawing to send with the letter.'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            padding: '9px 15px', background: '#0f2942', color: 'white', border: 'none',
            borderRadius: '8px', cursor: uploading ? 'wait' : 'pointer', fontSize: '13px',
            fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <FiUpload size={14} /> {uploading ? 'Uploading...' : 'Add Files'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          multiple
          onChange={(e) => handleUpload(e.target.files)}
          style={{ display: 'none' }}
        />
      </div>

      {!attachments?.length ? (
        <div style={{
          padding: '22px', textAlign: 'center', color: '#9ca3af', fontSize: '13px',
          border: '1px dashed #d1d5db', borderRadius: '8px', background: '#fafafa',
        }}>
          <FiPaperclip size={20} style={{ marginBottom: '6px' }} />
          <div>No enclosures yet. Files are listed on the letter and attached to the email.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {attachments.map((att, i) => {
            const busy = busyId === att.id;
            return (
              <div
                key={att.id}
                style={{
                  display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px',
                  border: '1px solid #e5e7eb', borderRadius: '8px',
                  background: busy ? '#f9fafb' : 'white', opacity: busy ? 0.6 : 1,
                }}
              >
                <span style={{
                  fontSize: '11px', fontWeight: 700, padding: '3px 9px', borderRadius: '999px',
                  background: '#eff6ff', color: '#1d4ed8', whiteSpace: 'nowrap', marginTop: '5px',
                }}>
                  Exhibit {att.exhibit_label || String.fromCharCode(65 + i)}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <input
                    value={drafts[att.id] !== undefined ? drafts[att.id] : (att.label || '')}
                    onChange={(e) => setDrafts(prev => ({ ...prev, [att.id]: e.target.value }))}
                    onBlur={() => saveLabel(att)}
                    placeholder="What this exhibit is — e.g. Site Sketch"
                    style={{
                      width: '100%', padding: '7px 9px', border: '1px solid #d1d5db',
                      borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                  />
                  <div style={{
                    fontSize: '11.5px', color: '#6b7280', marginTop: '5px',
                    display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                  }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>
                      {att.original_filename}
                    </span>
                    {att.file_size ? <span>· {formatSize(att.file_size)}</span> : null}
                    <button
                      type="button"
                      onClick={() => download(att)}
                      style={{
                        border: 'none', background: 'none', padding: 0, color: '#1d4ed8',
                        cursor: 'pointer', fontSize: '11.5px', display: 'inline-flex',
                        alignItems: 'center', gap: '3px',
                      }}
                    >
                      <FiDownload size={11} /> Open
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                    style={iconBtn(i === 0)}><FiArrowUp size={13} /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === attachments.length - 1} title="Move down"
                    style={iconBtn(i === attachments.length - 1)}><FiArrowDown size={13} /></button>
                  <button type="button" onClick={() => remove(att)} title="Remove"
                    style={{ ...iconBtn(false), color: '#dc2626' }}><FiTrash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const iconBtn = (disabled) => ({
  padding: '5px 7px', border: '1px solid #e5e7eb', borderRadius: '5px',
  background: 'white', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.35 : 1, color: '#374151', lineHeight: 0,
});

export default LetterAttachmentPanel;
